import {
    AxiosDefaults,
    AxiosError,
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig
} from 'axios'

import { authApi } from './api'
import { getToken, removeSessionCookies } from "../util/tokenCookies.ts";

type FailedRequestQueue = {
    onSuccess: (token: string) => void
    onFailure: (error: AxiosError) => void
}

const failedRequestQueue: FailedRequestQueue[] = []

// Prevent dispatching multiple logout events in quick succession which can
// cause navigation loops when many background requests return 401.
let logoutDispatched = false

type SetAuthorizationHeaderParams = {
    request: AxiosDefaults | AxiosRequestConfig
    token: string
}

export function setAuthorizationHeader(params: SetAuthorizationHeaderParams) {
    const { request, token } = params

    ;(request.headers as Record<string, unknown>)[
        'Authorization'
        ] = `Bearer ${token}`
}

function onRequest(config: AxiosRequestConfig) {
    const token = getToken()

    if (token) {
        setAuthorizationHeader({ request: config, token })
    }

    return config as InternalAxiosRequestConfig
}

function onRequestError(error: AxiosError): Promise<AxiosError> {
    return Promise.reject(error)
}

function onResponse(response: AxiosResponse): AxiosResponse {
    return response
}

type ErrorCode = {
    code: string
}

function onResponseError(
    error: AxiosError<ErrorCode>
): Promise<AxiosError | AxiosResponse> {
    const status = error?.response?.status;

    if (status === 401) {
        const errorCode = error?.response?.data?.code;

        // Handle token expiration
        if (errorCode === 'token.expired') {
            const originalConfig = error.config as AxiosRequestConfig;

            return new Promise((resolve, reject) => {
                failedRequestQueue.push({
                    onSuccess: (token: string) => {
                        // Retry the original request with the new token
                        setAuthorizationHeader({ request: originalConfig, token });
                        resolve(authApi(originalConfig));
                    },
                    onFailure: (error: AxiosError) => {
                        reject(error);
                    }
                });
            });
        }

        // Handle other 401 errors (e.g., invalid credentials during login)
        if (error?.config?.url?.includes('/auth/login')) {
            return Promise.reject(error);
        }

        // Handle other 401 errors (e.g., unauthorized access to protected resources)
        // Clear session cookies so we don't keep an invalid token.
        // Previously we forced a hard navigation to '/', which could trigger
        // route guards and cause an infinite redirect loop when background
        // queries (like subscription details) repeatedly returned 401.
        // Instead, just clear session cookies and reject so the AuthContext
        // (or caller) can handle navigation in a controlled way.
        removeSessionCookies();
        // Notify the app that the session is no longer valid so React can handle
        // navigation and cleanup in a single place (AuthContext).
        try {
            // Do not dispatch if we're already on the login page
            if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
                if (!logoutDispatched) {
                    logoutDispatched = true;
                    window.dispatchEvent(new CustomEvent('silvertalent:logout'));
                    // allow another logout dispatch after a short interval
                    setTimeout(() => { logoutDispatched = false }, 2000);
                }
            }
        } catch (e) {
            // ignore in environments where window isn't available
        }

        return Promise.reject(error);
    }

    return Promise.reject(error);
}

export function setupInterceptors(axiosInstance: AxiosInstance): AxiosInstance {
    axiosInstance.interceptors.request.use(onRequest, onRequestError)
    axiosInstance.interceptors.response.use(onResponse, onResponseError)

    return axiosInstance
}
